import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import {
  LayoutDashboard, Users, LinkIcon, ShoppingBag, BookOpen, Salad,
  MessagesSquare, MessageCircle, Webhook, LogOut, Home, Mail, Send, Inbox,
  UserPlus, CreditCard, Ticket, Zap, ChevronDown, Megaphone, Settings, FolderOpen,
  TrendingUp, Map, Gauge, Brain, DollarSign, Bell, HardDrive, Sparkles, Tag, Facebook,
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Grouped navigation — each group is collapsible.
const GROUPS = [
  {
    key: "overview",
    label: "نظرة عامة",
    icon: LayoutDashboard,
    always_open: true,   // never collapse — always show these top-level items
    items: [
      { to: "/admin", label: "الرئيسية", icon: LayoutDashboard, testid: "admin-nav-home", end: true, roles: ["admin", "super_admin"] },
      { to: "/admin/system", label: "حالة النظام", icon: Settings, testid: "admin-nav-system", roles: ["admin", "super_admin"] },
      { to: "/admin/usage", label: "استهلاك الخدمات", icon: Gauge, testid: "admin-nav-usage", roles: ["admin", "super_admin"] },
      { to: "/admin/media", label: "مكتبة الوسائط", icon: HardDrive, testid: "admin-nav-media", roles: ["admin", "super_admin"] },
      { to: "/admin/user-usage", label: "استخدام الأعضاء", icon: DollarSign, testid: "admin-nav-user-usage", roles: ["admin", "super_admin"] },
      { to: "/admin/ai-knowledge", label: "ذكاء المدرب", icon: Brain, testid: "admin-nav-ai-knowledge", roles: ["admin", "super_admin"] },
      { to: "/admin/sitemap", label: "خريطة الموقع", icon: Map, testid: "admin-nav-sitemap", roles: ["admin", "super_admin"] },
      { to: "/admin/automated-flows", label: "التدفقات التلقائية", icon: Zap, testid: "admin-nav-flows", roles: ["admin", "super_admin"] },
    ],
  },
  {
    key: "members",
    label: "الأعضاء والعملاء",
    icon: Users,
    items: [
      { to: "/admin/leads", label: "العملاء المحتملون", icon: UserPlus, testid: "admin-nav-leads", roles: ["admin", "super_admin", "moderator"] },
      { to: "/admin/journey", label: "رحلة العميل", icon: Zap, testid: "admin-nav-journey", roles: ["admin", "super_admin"] },
      { to: "/admin/members", label: "الأعضاء", icon: Users, testid: "admin-nav-members", roles: ["admin", "super_admin"] },
      { to: "/admin/invites", label: "روابط التفعيل", icon: LinkIcon, testid: "admin-nav-invites", roles: ["admin", "super_admin"] },
      { to: "/admin/legacy-imports", label: "استيراد أعضاء قدامى", icon: Users, testid: "admin-nav-legacy-imports", roles: ["admin", "super_admin"] },
    ],
  },
  {
    key: "sales",
    label: "المبيعات والدفع",
    icon: CreditCard,
    items: [
      { to: "/admin/purchases", label: "المشتريات", icon: ShoppingBag, testid: "admin-nav-purchases", roles: ["admin", "super_admin"] },
      { to: "/admin/subscriptions", label: "الاشتراكات", icon: CreditCard, testid: "admin-nav-subscriptions", roles: ["admin", "super_admin"] },
      { to: "/admin/attribution", label: "تحليلات التسويق", icon: TrendingUp, testid: "admin-nav-attribution", roles: ["admin", "super_admin"] },
      { to: "/admin/buyers", label: "قائمة Facebook Ads", icon: Facebook, testid: "admin-nav-buyers", roles: ["admin", "super_admin"] },
      { to: "/admin/plans", label: "خطط الاشتراك", icon: Tag, testid: "admin-nav-plans", roles: ["admin", "super_admin"] },
      { to: "/admin/coupons", label: "أكواد الخصم", icon: Ticket, testid: "admin-nav-coupons", roles: ["admin", "super_admin"] },
    ],
  },
  {
    key: "email",
    label: "التسويق بالبريد",
    icon: Mail,
    items: [
      { to: "/admin/inbox", label: "صندوق الوارد", icon: Inbox, testid: "admin-nav-inbox", roles: ["admin", "super_admin"] },
      { to: "/admin/emails/templates", label: "قوالب البريد", icon: Mail, testid: "admin-nav-emails-tpl", roles: ["admin", "super_admin"] },
      { to: "/admin/emails/sequences", label: "تسلسلات البريد", icon: Send, testid: "admin-nav-emails-seq", roles: ["admin", "super_admin"] },
      { to: "/admin/broadcasts", label: "الحملات البريدية", icon: Megaphone, testid: "admin-nav-broadcasts", roles: ["admin", "super_admin"] },
      { to: "/admin/notifications", label: "إشعارات التطبيق", icon: Bell, testid: "admin-nav-notifications", roles: ["admin", "super_admin"] },
      { to: "/admin/emails/queue", label: "سجل البريد", icon: Inbox, testid: "admin-nav-emails-queue", roles: ["admin", "super_admin"] },
    ],
  },
  {
    key: "whatsapp",
    label: "واتساب",
    icon: MessageCircle,
    items: [
      { to: "/admin/whatsapp", label: "لوحة واتساب", icon: MessageCircle, testid: "admin-nav-whatsapp", roles: ["admin", "super_admin"] },
    ],
  },
  {
    key: "content",
    label: "المحتوى والمجتمع",
    icon: FolderOpen,
    items: [
      { to: "/admin/courses", label: "الدورات", icon: BookOpen, testid: "admin-nav-courses", roles: ["admin", "super_admin"] },
      { to: "/admin/recipes", label: "الوصفات", icon: Salad, testid: "admin-nav-recipes", roles: ["admin", "super_admin"] },
      { to: "/admin/community", label: "المجتمع", icon: MessagesSquare, testid: "admin-nav-community", roles: ["admin", "super_admin", "moderator"] },
      { to: "/admin/chat", label: "الرسائل الخاصة", icon: MessageCircle, testid: "admin-nav-chat", roles: ["admin", "super_admin"] },
    ],
  },
  {
    key: "system",
    label: "النظام",
    icon: Settings,
    items: [
      { to: "/admin/webhooks", label: "سجل Webhooks", icon: Webhook, testid: "admin-nav-webhooks", roles: ["admin", "super_admin"] },
      { to: "/admin/diagnostics", label: "فحص الاتصالات", icon: Settings, testid: "admin-nav-diagnostics", roles: ["admin", "super_admin"] },
    ],
  },
];

// Persist open groups across visits
const OPEN_STORAGE_KEY = "admin_nav_open_groups_v1";
const loadOpenSet = () => {
  try {
    const raw = localStorage.getItem(OPEN_STORAGE_KEY);
    if (!raw) return new Set(["overview", "members", "sales", "email"]);
    return new Set(JSON.parse(raw));
  } catch { return new Set(["overview", "members", "sales", "email"]); }
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const userRoles = user?.roles || [];
  const [openGroups, setOpenGroups] = useState(loadOpenSet);

  // Filter groups + items by role; drop empty groups
  const visibleGroups = GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) => i.roles.some((r) => userRoles.includes(r))),
  })).filter((g) => g.items.length > 0);

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem(OPEN_STORAGE_KEY, JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  // Auto-open a group if the current URL matches one of its items
  const isGroupActive = (g) => g.items.some((i) => loc.pathname === i.to || (i.to !== "/admin" && loc.pathname.startsWith(i.to + "/")) || (i.to === "/admin" && loc.pathname === "/admin"));

  // Flat list for the mobile pill-nav (skip section headers on mobile)
  const flatLinks = visibleGroups.flatMap((g) => g.items);

  return (
    <div className="min-h-screen bg-[hsl(45_38%_90%)] text-foreground">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-lg border-b border-foreground/10">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-black text-white grid place-items-center font-display font-bold">إ</div>
            <span className="font-display text-lg font-semibold">لوحة الإدارة</span>
            <span className="text-xs text-foreground/50 me-2 hidden sm:inline">— {user?.full_name}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => nav("/dashboard")} className="gap-2 text-foreground hover:bg-foreground/5" data-testid="admin-btn-goto-member">
              <Home className="h-4 w-4" /> عرض كعضو
            </Button>
            <Button variant="ghost" size="icon" onClick={logout} className="text-foreground hover:bg-foreground/5" data-testid="admin-btn-logout">
              <LogOut className="h-4 w-4 rtl:-scale-x-100" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 lg:px-6 py-6 flex gap-6">
        <aside className="w-64 shrink-0 hidden md:block">
          <nav className="sticky top-24 space-y-1" data-testid="admin-side-nav">
            {visibleGroups.map((g) => {
              const alwaysOpen = g.always_open;
              const isOpen = alwaysOpen || openGroups.has(g.key) || isGroupActive(g);
              return (
                <div key={g.key} data-testid={`admin-nav-group-${g.key}`}>
                  {!alwaysOpen && (
                    <button
                      type="button"
                      onClick={() => toggleGroup(g.key)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-[11px] font-semibold uppercase tracking-wider text-foreground/50 hover:text-foreground hover:bg-white/60 transition-colors"
                      data-testid={`admin-nav-group-toggle-${g.key}`}
                    >
                      <span className="flex items-center gap-2">
                        <g.icon className="h-3.5 w-3.5" />
                        <span>{g.label}</span>
                      </span>
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "" : "-rotate-90 rtl:rotate-90"}`} />
                    </button>
                  )}
                  {alwaysOpen && (
                    <div className="px-3 pt-1 pb-0.5 text-[11px] font-semibold uppercase tracking-wider text-foreground/40 flex items-center gap-2">
                      <g.icon className="h-3.5 w-3.5" />
                      <span>{g.label}</span>
                    </div>
                  )}
                  {isOpen && (
                    <div className="mt-0.5 mb-1 space-y-0.5">
                      {g.items.map((l) => (
                        <NavLink
                          key={l.to}
                          to={l.to}
                          end={l.end}
                          data-testid={l.testid}
                          className={({ isActive }) =>
                            `flex items-center gap-3 ps-6 pe-3 py-2 rounded-lg text-sm transition-colors ${
                              isActive
                                ? "bg-black text-white font-medium"
                                : "text-foreground hover:bg-white"
                            }`
                          }
                        >
                          <l.icon className="h-4 w-4 rtl:-scale-x-100 opacity-80" />
                          <span>{l.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 min-w-0">
          <nav className="md:hidden flex gap-1 overflow-x-auto pb-2 mb-4 -mx-4 px-4" data-testid="admin-mobile-nav">
            {flatLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `whitespace-nowrap px-3 py-1.5 rounded-full text-xs ${
                    isActive ? "bg-black text-white" : "bg-white text-foreground"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
